import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import bedrockService from '../services/bedrockService';
import { AuthContext } from '../contexts/AuthContext';
import SearchableDropdown from './SearchableDropdown';
import { getExtractionStatus } from './DataPointsTable';
import AccountTable, { RerunButton } from './AccountTable';
import ClientDocumentAsk from './ClientDocumentAsk';
import BulkRerunBanner from './BulkRerunBanner';
import {
  EMPTY_ANNOTATIONS, applyCorrections, buildClientReport, completionOf, optimisticAnnotations, refreshSyncStaleness, samePath,
  valueEntriesOf,
} from '../utils/clientReport';
import { filterReport, searchReport } from '../utils/reportSearch';
import { displayNameOf } from '../utils/people';
import { buildExportModel, exportFilename } from '../utils/reportExport';
import { downloadBlob, readBlobText } from '../utils/downloadBlob';
import './ClientDocumentReport.css';

export const RERUN_POLL_MS = 3000;
export const RERUN_TIMEOUT_MS = 180000;
// How often the open report picks up other people's corrections, eMoney
// marks and notes.
export const ANNOTATION_POLL_MS = 30000;
// The poll reads an index that can trail a save by a moment; a document
// saved from this page more recently than this keeps what the save returned.
const RECENT_SAVE_MS = 5000;

const RERUN_MESSAGES = {
  extracted: 'Updated',
  not_applicable: 'Updated: the prompt says this document is not applicable',
  parse_error: "The prompt's reply wasn't valid JSON; the previous data was kept",
  none: 'No extraction prompt applies to this document type',
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ANNOTATION_SAVERS = {
  correction: (fileId, payload) => bedrockService.saveCorrection(fileId, payload),
  sync: (fileId, payload) => bedrockService.saveSyncMark(fileId, payload),
  note: (fileId, payload) => bedrockService.saveNote(fileId, payload),
};

// The report shows corrected values; the raw extraction stays on llmDataPoints.
const withCorrections = (doc) => {
  const corrections = doc.annotations && doc.annotations.corrections;
  if (!corrections || !corrections.length) return doc;
  return { ...doc, llmDataPoints: doc.dataPoints, dataPoints: applyCorrections(doc.dataPoints, corrections) };
};

const STATUS_BADGES = {
  not_applicable: { className: 'bg-secondary', label: 'Not applicable' },
  failed: { className: 'bg-warning text-dark', label: 'Extraction failed' },
  none: { className: 'bg-light text-dark border', label: 'Summary only' },
};

const sectionAnchor = (key) => `client-report-section-${key}`;

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToSection = (event, key) => {
  event.preventDefault();
  scrollToId(sectionAnchor(key));
};

// Account keys contain "|", so search jumps use an index-based element id.
const accountAnchor = (index) => `client-report-account-${index}`;

const DocumentWithoutData = ({ doc, onRerun, rerunState }) => {
  const extraction = getExtractionStatus(doc.dataPoints, doc.dataPointsDetails);
  const badge = STATUS_BADGES[extraction.kind] || STATUS_BADGES.none;
  const promptName = doc.dataPointsDetails && doc.dataPointsDetails.prompt_name;
  return (
    <li id={`without-data-${doc.fileId}`} className="d-flex flex-wrap align-items-center gap-2 small mb-1" data-testid={`without-data-${doc.fileId}`}>
      <span className={`badge ${badge.className}`} title={extraction.reason || undefined}>{badge.label}</span>
      <span>{doc.documentName || doc.fileId}</span>
      {extraction.reason && <span className="text-muted">{extraction.reason}</span>}
      {promptName && <span className="text-muted d-print-none">From <code>{promptName}</code></span>}
      {doc.url && <a href={doc.url} target="_blank" rel="noopener noreferrer" className="d-print-none">Open file</a>}
      <RerunButton fileId={doc.fileId} state={rerunState} onRerun={onRerun} />
    </li>
  );
};

/**
 * ClientDocumentReport
 *
 * The eMoney client report: a table of contents, then each report set
 * (Net Worth, Cash Flow / Protection, Reviewed Separately) with one section
 * per Build prompt and one table per account -- the most recent statement
 * for each account, older ones listed underneath. The PDF and Word exports
 * give the same report as a file. The Ask box answers questions across the
 * client's data when the documents knowledge base is configured.
 */
const ClientDocumentReport = () => {
  const auth = useContext(AuthContext);
  const canEdit = Boolean(auth && auth.hasPermission && auth.hasPermission('document_data_edit'));
  const user = auth && auth.user;
  const me = displayNameOf(user) || 'You';
  const viewer = useMemo(() => ({
    name: me,
    ids: user ? [user.user_id, user.username].filter(Boolean) : [],
  }), [me, user]);
  // Per document: the annotations the server last confirmed; the changes
  // still being saved, shown on top of them in order; the chain that sends
  // those saves one at a time; when the last save finished; and the last
  // successful save ({ seq, revision }; seq counts saves, so a reload can
  // tell which ones it may have missed).
  const confirmedRef = useRef({});
  const pendingRef = useRef({});
  const saveChainRef = useRef({});
  const lastSaveRef = useRef({});
  const saveSeqRef = useRef(0);
  const savedRef = useRef({});
  const loadClientRef = useRef(null);
  const meRef = useRef(me);
  meRef.current = me;
  const docsRef = useRef([]);
  // clients: [{ clientId, clientName }]. The report is keyed by clientId (the
  // Client Helper client); clientName is only shown.
  const [clients, setClients] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [selectedClient, setSelectedClient] = useState('');
  const [docs, setDocs] = useState([]);
  docsRef.current = docs;
  const [askEnabled, setAskEnabled] = useState(false);
  const [annotationsAvailable, setAnnotationsAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const [reruns, setReruns] = useState({});
  // Running bulk "entered in eMoney" saves: [{ scope, fileIds }].
  const [bulkBusy, setBulkBusy] = useState([]);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'docx' while a file is being made
  const [query, setQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(-1);
  const didInit = useRef(false);
  // Bumped on client change and unmount so an in-flight re-run poll stops
  // without touching state that now belongs to another client.
  const clientRunRef = useRef(0);

  useEffect(() => () => { clientRunRef.current += 1; }, []);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    bedrockService.getExtractionClients()
      .then((data) => {
        setClients(data.clients || []);
        setUnassignedCount(data.unassignedCount || 0);
      })
      .catch((err) => {
        console.error('Error loading clients:', err);
        setError('Failed to load the client list.');
      });
  }, []);

  // A document's annotations as shown: what the server confirmed, with the
  // changes still being saved applied on top, in the order they were made.
  // A change made against an earlier run of the document (revision: the one
  // shown now) isn't shown: its path may name a different account now. It
  // stays pending; the server refuses it (409) and the editor says why.
  // A pending correction changes a value, so the entered-in-eMoney marks are
  // re-checked against it (dataPoints: the document's raw extraction).
  const shownAnnotations = useCallback((fileId, dataPoints, revision) => {
    const pending = (pendingRef.current[fileId] || []).filter(({ payload }) => (
      !payload.extractionRevision || !revision || payload.extractionRevision === revision
    ));
    const confirmed = confirmedRef.current[fileId] || EMPTY_ANNOTATIONS;
    if (!pending.length) return confirmed;
    return refreshSyncStaleness(pending.reduce(
      (current, change) => optimisticAnnotations(current, change.kind, change.payload, { by: meRef.current, original: change.hint.original }),
      confirmed,
    ), dataPoints);
  }, []);

  // quiet: refresh in place (after a re-run) without swapping the report
  // for a loading spinner.
  const loadClient = useCallback(async (clientId, { quiet = false } = {}) => {
    if (!clientId) return;
    if (!quiet) setLoading(true);
    setError('');
    const startSeq = saveSeqRef.current;
    try {
      const data = await bedrockService.getExtractions({
        clientId,
        includeUnextracted: true,
        includeSummary: true,
      });
      const extractions = data.extractions || [];
      // A save confirmed while this load was in flight may be newer than the
      // annotations it read: keep the save's answer when it was made against
      // the same run of the document. Against another run, take the load and
      // load once more so data and annotations agree.
      const before = confirmedRef.current;
      let settle = false;
      confirmedRef.current = Object.fromEntries(extractions.map((doc) => {
        const saved = savedRef.current[doc.fileId];
        if (saved && saved.seq > startSeq && before[doc.fileId]) {
          if (!saved.revision || saved.revision === doc.extractionRevision) return [doc.fileId, before[doc.fileId]];
          settle = true;
        }
        return [doc.fileId, doc.annotations || EMPTY_ANNOTATIONS];
      }));
      // Changes still being saved stay on top of the reloaded report.
      setDocs(extractions.map((doc) => (
        { ...doc, annotations: shownAnnotations(doc.fileId, doc.dataPoints, doc.extractionRevision) }
      )));
      setAskEnabled(Boolean(data.askEnabled));
      setAnnotationsAvailable(data.annotationsAvailable !== false);
      if (settle) loadClientRef.current(clientId, { quiet: true });
    } catch (err) {
      console.error('Error loading client documents:', err);
      setError('Failed to load documents for this client.');
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [shownAnnotations]);
  loadClientRef.current = loadClient;

  const selectedName = (clients.find((c) => c.clientId === selectedClient) || {}).clientName || '';

  // The picker shows names (unique: the server adds part of the id when two
  // clients would show the same one); the report runs on the id.
  const handleClientChange = (clientName) => {
    const clientId = (clients.find((c) => c.clientName === clientName) || {}).clientId || '';
    clientRunRef.current += 1;
    confirmedRef.current = {};
    pendingRef.current = {};
    saveChainRef.current = {};
    lastSaveRef.current = {};
    savedRef.current = {};
    setSelectedClient(clientId);
    setCollapsedSections(new Set());
    setReruns({});
    setQuery('');
    setMatchIndex(-1);
    loadClient(clientId);
  };

  const setRerun = (fileId, value) => setReruns((prev) => ({ ...prev, [fileId]: value }));

  // Re-run one document's extraction, wait for it, then refresh the report.
  const handleRerun = async (fileId) => {
    const clientRun = clientRunRef.current;
    const clientName = selectedClient;
    const isCurrent = () => clientRunRef.current === clientRun;
    setRerun(fileId, { state: 'running' });
    try {
      const { job_id: jobId } = await bedrockService.rerunExtraction(fileId);
      const deadline = Date.now() + RERUN_TIMEOUT_MS;
      for (;;) {
        await sleep(RERUN_POLL_MS);
        if (!isCurrent()) return;
        const job = await bedrockService.getExtractionRerun(jobId);
        if (!isCurrent()) return;
        if (job.status === 'completed') {
          await loadClient(clientName, { quiet: true });
          if (!isCurrent()) return;
          let message = RERUN_MESSAGES[job.extraction_status] || 'Updated';
          if (job.corrections_unplaced) {
            message += job.corrections_unplaced === 1
              ? '; 1 correction no longer matches a field'
              : `; ${job.corrections_unplaced} corrections no longer match a field`;
          }
          setRerun(fileId, { state: 'done', message });
          return;
        }
        if (job.status === 'failed') {
          setRerun(fileId, { state: 'failed', message: job.error || 'The re-run failed.' });
          return;
        }
        if (Date.now() > deadline) {
          setRerun(fileId, { state: 'failed', message: 'Still running. Use Refresh in a minute.' });
          return;
        }
      }
    } catch (err) {
      console.error('Error re-running extraction:', err);
      if (isCurrent()) {
        const detail = err && err.response && err.response.data && err.response.data.detail;
        setRerun(fileId, { state: 'failed', message: detail || 'Could not start the re-run.' });
      }
    }
  };

  const showAnnotations = (fileId) => setDocs((prev) => prev.map((doc) => (
    doc.fileId === fileId ? { ...doc, annotations: shownAnnotations(fileId, doc.dataPoints, doc.extractionRevision) } : doc
  )));

  // Save a correction, entered-in-eMoney mark or note. The change shows at
  // once, on top of what the server confirmed. Saves on one document are
  // sent one at a time, so each sees the ones before it; each reply becomes
  // the confirmed state and only that change leaves the pending list, so a
  // failure takes back just its own change. Resolves to an error message,
  // or null. When someone else changed it first (409) the report reloads so
  // the table shows their change. hint.original: the value being corrected,
  // for the "edited" badge.
  const handleAnnotate = (fileId, kind, payload, hint = {}) => {
    const clientRun = clientRunRef.current;
    const isCurrent = () => clientRunRef.current === clientRun;
    const change = { kind, payload, hint };
    pendingRef.current[fileId] = [...(pendingRef.current[fileId] || []), change];
    showAnnotations(fileId);
    const save = async () => {
      try {
        const data = await ANNOTATION_SAVERS[kind](fileId, payload);
        if (isCurrent()) {
          confirmedRef.current[fileId] = data.annotations;
          saveSeqRef.current += 1;
          savedRef.current[fileId] = { seq: saveSeqRef.current, revision: payload.extractionRevision };
        }
        return null;
      } catch (err) {
        console.error(`Error saving the ${kind}:`, err);
        const response = err && err.response;
        const detail = response && response.data && response.data.detail;
        if (response && response.status === 409 && isCurrent()) {
          loadClient(selectedClient, { quiet: true });
        }
        if (response && response.status === 403) return "You don't have permission to edit this report.";
        return (typeof detail === 'string' && detail) || 'Could not save the change. Try again.';
      } finally {
        if (isCurrent()) {
          pendingRef.current[fileId] = (pendingRef.current[fileId] || []).filter((c) => c !== change);
          lastSaveRef.current[fileId] = Date.now();
          showAnnotations(fileId);
        }
      }
    };
    const result = (saveChainRef.current[fileId] || Promise.resolve()).then(save);
    saveChainRef.current[fileId] = result;
    return result;
  };

  // Mark (or unmark) many values as entered in eMoney at once: a section, an
  // account, a nested table or row. One save per document, each in that
  // document's queue so it stays in order with single checks; documents run
  // side by side. Nothing flips while they run (the counts show a spinner);
  // once every document has answered the report updates in one go. Resolves
  // to an error message, or null. targets: [{ doc, entry }].
  const handleBulkSync = async (scope, targets, synced) => {
    const clientRun = clientRunRef.current;
    const isCurrent = () => clientRunRef.current === clientRun;
    const byFile = new Map();
    targets.forEach(({ doc, entry }) => {
      const mark = ((doc.annotations || EMPTY_ANNOTATIONS).sync || []).find((m) => samePath(m.path, entry.syncPath));
      if (Boolean(mark && !mark.stale) === synced) return; // already as asked
      if (!byFile.has(doc.fileId)) byFile.set(doc.fileId, { doc, marks: [] });
      byFile.get(doc.fileId).marks.push({ path: entry.syncPath, expectedValue: entry.value === undefined ? null : entry.value });
    });
    if (!byFile.size) return null;
    const running = { scope, fileIds: [...byFile.keys()] };
    setBulkBusy((prev) => [...prev, running]);

    const saveDocument = ({ doc, marks }) => {
      const { fileId } = doc;
      const save = async () => {
        try {
          const data = await bedrockService.saveSyncMarks(fileId, { marks, synced, extractionRevision: doc.extractionRevision });
          if (isCurrent()) {
            confirmedRef.current[fileId] = data.annotations;
            saveSeqRef.current += 1;
            savedRef.current[fileId] = { seq: saveSeqRef.current, revision: doc.extractionRevision };
          }
          return null;
        } catch (err) {
          console.error('Error saving the eMoney checks:', err);
          const response = err && err.response;
          const detail = response && response.data && response.data.detail;
          if (response && response.status === 409 && isCurrent()) {
            loadClient(selectedClient, { quiet: true });
          }
          if (response && response.status === 403) return "You don't have permission to edit this report.";
          return (typeof detail === 'string' && detail) || 'Could not save the checks. Try again.';
        } finally {
          if (isCurrent()) lastSaveRef.current[fileId] = Date.now();
        }
      };
      const result = (saveChainRef.current[fileId] || Promise.resolve()).then(save);
      saveChainRef.current[fileId] = result;
      return result;
    };

    const messages = await Promise.all([...byFile.values()].map(saveDocument));
    if (!isCurrent()) return null;
    const saved = new Set(byFile.keys());
    setDocs((prev) => prev.map((doc) => (
      saved.has(doc.fileId) ? { ...doc, annotations: shownAnnotations(doc.fileId, doc.dataPoints, doc.extractionRevision) } : doc
    )));
    setBulkBusy((prev) => prev.filter((b) => b !== running));
    return messages.find(Boolean) || null;
  };

  const handleSectionComplete = async (section, targets, complete) => {
    setError('');
    const message = await handleBulkSync(`section:${section.key}`, targets, complete);
    if (message) setError(message);
  };

  // Pick up other people's changes while the report is open (and the tab is
  // visible), without reloading the documents themselves.
  useEffect(() => {
    if (!selectedClient) return undefined;
    const clientRun = clientRunRef.current;
    const poll = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const startedAt = Date.now();
      try {
        const data = await bedrockService.getClientAnnotations(selectedClient);
        if (clientRunRef.current !== clientRun || !data) return;
        // The poll's paths fit the server's current extraction. If a document
        // was re-run since this page loaded it, reload instead of placing
        // them on the old data.
        const revisions = data.revisions || {};
        const rerun = docsRef.current.some((doc) => (
          doc.extractionRevision && revisions[doc.fileId] && revisions[doc.fileId] !== doc.extractionRevision
        ));
        if (rerun) {
          loadClient(selectedClient, { quiet: true });
          return;
        }
        const latest = data.annotations || {};
        setDocs((prev) => {
          let changed = false;
          const next = prev.map((doc) => {
            const busy = (pendingRef.current[doc.fileId] || []).length > 0
              || (lastSaveRef.current[doc.fileId] || 0) > startedAt - RECENT_SAVE_MS;
            if (busy) return doc;
            const annotations = latest[doc.fileId] || EMPTY_ANNOTATIONS;
            confirmedRef.current[doc.fileId] = annotations;
            if (JSON.stringify(annotations) === JSON.stringify(doc.annotations || EMPTY_ANNOTATIONS)) return doc;
            changed = true;
            return { ...doc, annotations };
          });
          return changed ? next : prev;
        });
      } catch (err) {
        console.warn('Could not check for report changes:', err);
      }
    };
    const timer = setInterval(poll, ANNOTATION_POLL_MS);
    return () => clearInterval(timer);
  }, [selectedClient, loadClient]);

  const correctedDocs = useMemo(() => docs.map(withCorrections), [docs]);
  const report = useMemo(() => buildClientReport(correctedDocs), [correctedDocs]);
  const allSectionKeys = useMemo(
    () => report.flatMap((set) => set.sections.map((section) => section.key)),
    [report]
  );
  // Element ids for every account, by position in the full report.
  const accountIds = useMemo(() => {
    const ids = new Map();
    report.forEach((set) => set.sections.forEach((section) => section.accounts.forEach((account) => {
      ids.set(account.key, accountAnchor(ids.size));
    })));
    return ids;
  }, [report]);

  const search = useMemo(() => searchReport(report, query), [report, query]);
  const shownReport = useMemo(() => filterReport(report, search), [report, search]);
  const searching = Boolean(search);
  // Matches in reading order, for Enter to step through.
  const matchIds = useMemo(() => (search
    ? shownReport.flatMap((set) => set.sections.flatMap((section) => [
      ...section.accounts.map((account) => accountIds.get(account.key)),
      ...section.withoutData.map((doc) => `without-data-${doc.fileId}`),
    ]))
    : []), [search, shownReport, accountIds]);

  const handleQueryChange = (value) => {
    setQuery(value);
    setMatchIndex(-1);
  };

  const jumpToNextMatch = () => {
    if (!matchIds.length) return;
    const next = (matchIndex + 1) % matchIds.length;
    setMatchIndex(next);
    scrollToId(matchIds[next]);
  };

  const toggleSection = (key) => setCollapsedSections((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // PDF / Word file of the whole report (a search doesn't narrow it): the
  // page builds what the report shows and the server lays it out.
  const handleExport = async (format) => {
    if (loading) return;
    setExporting(format);
    setError('');
    try {
      const model = buildExportModel(report, { clientName: selectedName, viewer });
      const blob = await bedrockService.exportReport(format, model);
      downloadBlob(blob, exportFilename(selectedName, format));
    } catch (err) {
      console.error(`Error exporting the report (${format}):`, err);
      let detail = null;
      const data = err && err.response && err.response.data;
      // The response was requested as a Blob, so an error body is one too.
      if (data instanceof Blob) {
        try { detail = JSON.parse(await readBlobText(data)).detail; } catch (parseError) { detail = null; }
      }
      setError(typeof detail === 'string' && detail ? detail : `Could not export the report as ${format === 'pdf' ? 'PDF' : 'Word'}. Try again.`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div data-testid="client-document-report">
      <div className="row g-2 align-items-end mb-3 d-print-none">
        <div className="col-md-5">
          <label className="form-label small mb-1"><strong>Client</strong></label>
          <SearchableDropdown
            options={clients.map((c) => c.clientName)}
            value={selectedName}
            onChange={handleClientChange}
            placeholder="-- choose client --"
            searchPlaceholder="Search clients..."
          />
          {unassignedCount > 0 && (
            <div className="form-text" data-testid="unassigned-documents">
              {unassignedCount} analyzed {unassignedCount === 1 ? 'document has' : 'documents have'} no client yet:
              push {unassignedCount === 1 ? 'it' : 'them'} with a client, or upload to a SharePoint folder that has one.
            </div>
          )}
        </div>
        {selectedClient && (
          <div className="col-md-7 d-flex flex-wrap gap-2 justify-content-md-end">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCollapsedSections(new Set())} disabled={!docs.length}>
              <i className="bi bi-arrows-expand me-1"></i>Expand all
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setCollapsedSections(new Set(allSectionKeys))} disabled={!docs.length}>
              <i className="bi bi-arrows-collapse me-1"></i>Collapse all
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => loadClient(selectedClient)}
              disabled={loading}
              title="File links expire after an hour; refresh to get new ones"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </button>
            <div className="btn-group btn-group-sm" role="group" aria-label="Export the report">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleExport('pdf')}
                disabled={!docs.length || Boolean(exporting)}
                title="Download the whole report as a PDF with a linked table of contents"
              >
                {exporting === 'pdf'
                  ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  : <i className="bi bi-file-earmark-pdf me-1"></i>}
                PDF
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => handleExport('docx')}
                disabled={!docs.length || Boolean(exporting)}
                title="Download the whole report as a Word document"
              >
                {exporting === 'docx'
                  ? <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  : <i className="bi bi-file-earmark-word me-1"></i>}
                Word
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="alert alert-danger py-2 d-print-none" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {selectedClient && !loading && !annotationsAvailable && (
        <div className="alert alert-warning py-2 d-print-none" data-testid="annotations-unavailable">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Corrections, notes and eMoney marks couldn&rsquo;t be loaded, so values are shown as extracted.
          Use Refresh to try again; editing is off until they load.
        </div>
      )}

      {selectedClient && (
        <BulkRerunBanner
          key={selectedClient}
          clientId={selectedClient}
          docs={docs}
          onReload={() => loadClient(selectedClient, { quiet: true })}
        />
      )}

      {selectedClient && askEnabled && (
        <div className="d-print-none"><ClientDocumentAsk clientId={selectedClient} clientName={selectedName} /></div>
      )}

      {selectedClient && !loading && report.length > 0 && (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 d-print-none" data-testid="report-search">
          <div className="input-group input-group-sm" style={{ maxWidth: 420 }}>
            <span className="input-group-text"><i className="bi bi-search"></i></span>
            <input
              type="search"
              className="form-control client-report-search"
              placeholder="Search this report (e.g. visa, 1234, cash balance)"
              aria-label="Search this report"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  jumpToNextMatch();
                }
              }}
            />
            {query && (
              <button type="button" className="btn btn-outline-secondary" onClick={() => handleQueryChange('')} aria-label="Clear search">
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
          {searching && (
            <span className="small text-muted" data-testid="report-search-count">
              {search.count === 1 ? '1 match' : `${search.count} matches`}
              {search.count > 1 && ' · Enter jumps to the next'}
            </span>
          )}
        </div>
      )}

      {!selectedClient ? (
        <div className="text-center text-muted border rounded p-4" data-testid="report-choose-client">
          <i className="bi bi-person-lines-fill fs-4 d-block mb-2"></i>
          Choose a client to build their eMoney report.
        </div>
      ) : loading ? (
        <div className="text-center text-muted border rounded p-4">
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Loading documents...
        </div>
      ) : report.length === 0 ? (
        <div className="text-center text-muted border rounded p-4" data-testid="report-empty">
          <i className="bi bi-database-x fs-4 d-block mb-2"></i>
          No analyzed documents for this client yet.
        </div>
      ) : shownReport.length === 0 ? (
        <div className="text-center text-muted border rounded p-4" data-testid="report-search-empty">
          <i className="bi bi-search fs-4 d-block mb-2"></i>
          Nothing in this report matches &ldquo;{query.trim()}&rdquo;.
        </div>
      ) : (
        <div className="client-report-body" data-testid="client-report-body">
          <h5 className="mb-3">
            {selectedName} <span className="text-muted fw-normal">· eMoney report</span>
          </h5>

          <nav className="border rounded p-2 mb-4" aria-label="Table of contents" data-testid="report-toc">
            <div className="small text-muted mb-1">Contents</div>
            {shownReport.map((set) => (
              <div key={set.key} className="mb-1">
                <strong className="small">{set.label}</strong>
                <ul className="list-inline mb-0 small">
                  {set.sections.map((section) => (
                    <li key={section.key} className="list-inline-item">
                      <a href={`#${sectionAnchor(section.key)}`} onClick={(e) => scrollToSection(e, section.key)}>
                        {section.label}
                      </a>
                      <span className="text-muted"> ({section.accounts.length})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {shownReport.map((set) => (
            <div key={set.key} className="client-report-set mb-4" data-testid="report-set">
              <h4 className="border-bottom pb-1" data-testid="report-set-title">{set.label}</h4>
              {set.sections.map((section) => {
                // A search opens every section with a match.
                const open = searching || !collapsedSections.has(section.key);
                const completionEntries = section.accounts.flatMap((account) => (
                  valueEntriesOf(account.current.fields, account.current.ref)
                    .map((entry) => ({ doc: account.current.doc, entry }))
                ));
                // Counted in items, like each account's count (a nested row counts once).
                const { done: completedCount, total: completionTotal } = completionOf(
                  completionEntries,
                  ({ doc, entry }) => ((doc.annotations || EMPTY_ANNOTATIONS).sync || []).find((item) => samePath(item.path, entry.syncPath)),
                  ({ doc, entry }) => `${doc.fileId}|${entry.item}`,
                );
                const sectionComplete = completionTotal > 0 && completedCount === completionTotal;
                // Any bulk save on this section's documents: its count waits for it.
                const sectionFileIds = new Set(section.accounts.map((account) => account.current.doc.fileId));
                const sectionBusy = bulkBusy.some((b) => b.fileIds.some((id) => sectionFileIds.has(id)));
                return (
                  <section key={section.key} id={sectionAnchor(section.key)} className="mb-3" data-testid="report-section">
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-light w-100 text-start d-flex align-items-center gap-2"
                        onClick={() => toggleSection(section.key)}
                        aria-expanded={open}
                      >
                        <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} d-print-none`}></i>
                        <strong data-testid="report-section-title">{section.label}</strong>
                        <span className="badge bg-secondary ms-1">
                          {section.accounts.length} {section.accounts.length === 1 ? 'account' : 'accounts'}
                        </span>
                      </button>
                      {canEdit && annotationsAvailable && completionEntries.length > 0 && (
                        <label className="small d-print-none d-inline-flex align-items-center gap-1 mb-0 text-nowrap">
                          <input
                            type="checkbox"
                            className="form-check-input mt-0"
                            aria-label={`Mark ${section.label} complete`}
                            checked={sectionComplete}
                            disabled={sectionBusy}
                            onChange={(event) => handleSectionComplete(section, completionEntries, event.target.checked)}
                          />
                          {sectionBusy ? (
                            <span className="text-muted" role="status" data-testid="section-saving">
                              <span className="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Saving…
                            </span>
                          ) : (
                            <span data-testid="section-count">{completedCount}/{completionTotal} complete</span>
                          )}
                        </label>
                      )}
                    </div>
                    {open && (
                      <div className="pt-2">
                        {section.accounts.map((account) => (
                          <AccountTable
                            key={account.key}
                            account={account}
                            onRerun={handleRerun}
                            rerunState={reruns[account.current.doc.fileId]}
                            tokens={search ? search.tokens : null}
                            domId={accountIds.get(account.key)}
                            canEdit={canEdit && annotationsAvailable}
                            viewer={viewer}
                            onAnnotate={handleAnnotate}
                            onBulkSync={handleBulkSync}
                            bulkBusy={bulkBusy}
                            onLoadHistory={bedrockService.getDocumentHistory}
                          />
                        ))}
                        {section.withoutData.length > 0 && (
                          <div className="small">
                            <div className="text-muted mb-1">
                              {section.accounts.length ? 'Also in this section, without extracted data:' : 'Documents without extracted data:'}
                            </div>
                            <ul className="list-unstyled mb-0">
                              {section.withoutData.map((doc) => (
                                <DocumentWithoutData
                                  key={doc.fileId}
                                  doc={doc}
                                  onRerun={handleRerun}
                                  rerunState={reruns[doc.fileId]}
                                />
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientDocumentReport;
